import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { path: '/', label: 'Accueil' },
    { path: '/assurances', label: 'Assurances' },
    { path: '/fiscalite', label: 'Fiscalité' },
    { path: '/retraite', label: 'Retraite / Prévoyance' },
    { path: '/investissements', label: 'Investissements' },
    { path: '/immobilier', label: 'Immobilier' },
    { path: '/credits-prives', label: 'Crédits privés' },
    { 
      label: 'Solutions Entreprises',
      submenu: [
        { path: '/mindfinance', label: 'MindFinance', icon: '🌿' },
        { path: '/pme', label: 'Indépendants & PME' }
      ]
    },
    { path: '/guides', label: 'Guides' },
    { path: '/a-propos', label: 'A propos' },
    { path: '/contact', label: 'Contact' }
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'glass backdrop-blur-lg' : 'bg-transparent'
    }`}>
      <div className="container mx-auto px-4 lg:px-6">
        <nav className="flex items-center justify-between py-4 pr-[25px]">
          {/* Logo */}
          <Link to="/" className="text-2xl font-bold heading-display text-white flex-shrink-0 flex items-center" data-testid="header-logo-link">
            <img 
              src="/assets/logo/atom-logo-white.png" 
              alt="ATOM Group SA" 
              className="h-8 w-auto"
              data-testid="header-logo-image"
            />
          </Link>

          {/* Navigation desktop */}
          <div className="hidden lg:flex items-center gap-x-1 lg:gap-x-1.5 xl:gap-x-2 flex-shrink-0 ml-6">
            {navItems.map((item, index) => (
              item.submenu ? (
                <div key={index} className="relative group">
                  <button className="text-[10px] lg:text-[11px] xl:text-xs font-medium text-gray-300 hover:text-white transition-colors duration-200 whitespace-nowrap flex items-center px-0.5">
                    {item.label}
                    <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <div className="absolute left-0 mt-2 w-56 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <div className="glass rounded-xl shadow-xl py-2">
                      {item.submenu.map((subItem) => (
                        <Link
                          key={subItem.path}
                          to={subItem.path}
                          className={`block px-4 py-2 text-sm transition-colors duration-200 ${
                            location.pathname === subItem.path
                              ? 'text-white bg-white/10'
                              : 'text-gray-300 hover:text-white hover:bg-white/5'
                          }`}
                        >
                          {subItem.icon && <span className="mr-2">{subItem.icon}</span>}
                          {subItem.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`text-[10px] lg:text-[11px] xl:text-xs font-medium transition-colors duration-200 whitespace-nowrap px-0.5 ${
                    location.pathname === item.path
                      ? 'text-white'
                      : 'text-gray-300 hover:text-white'
                  }`}
                  data-testid={`nav-link-${item.path.replace('/', '') || 'home'}`}
                >
                  {item.label}
                </Link>
              )
            ))}
          </div>

          {/* Menu mobile */}
          <button
            className="lg:hidden p-2 text-white"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>

        {/* Menu mobile déroulant */}
        {isMenuOpen && (
          <div className="lg:hidden glass mt-4 p-6 rounded-2xl" data-testid="mobile-menu-dropdown">
            <div className="flex flex-col space-y-4">
              {navItems.map((item, index) => (
                item.submenu ? (
                  <div key={index}>
                    <div className="text-base font-medium text-gray-400 mb-2">{item.label}</div>
                    <div className="pl-4 space-y-2">
                      {item.submenu.map((subItem) => (
                        <Link
                          key={subItem.path}
                          to={subItem.path}
                          className={`block text-base font-medium transition-colors duration-200 ${
                            location.pathname === subItem.path
                              ? 'text-white'
                              : 'text-gray-300 hover:text-white'
                          }`}
                          onClick={() => setIsMenuOpen(false)}
                        >
                          {subItem.icon && <span className="mr-2">{subItem.icon}</span>}
                          {subItem.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`text-base font-medium transition-colors duration-200 ${
                      location.pathname === item.path
                        ? 'text-white'
                        : 'text-gray-300 hover:text-white'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                    data-testid={`mobile-nav-link-${item.path.replace('/', '') || 'home'}`}
                  >
                    {item.label}
                  </Link>
                )
              ))}
              <Link
                to="/contact"
                className="btn-glass btn-primary mt-4 text-center"
                onClick={() => setIsMenuOpen(false)}
                data-testid="mobile-cta-contact"
              >
                Demander un conseil
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;